import MenuIcon from '@mui/icons-material/Menu';
import {AppBar, Box, IconButton, Stack, Theme, Toolbar, Typography} from '@mui/material';
import {forwardRef} from 'react';
import {ComponentBoundary} from '../../common/ComponentBoundary';
import {useResponsiveAppName} from '../../common/hooks/useAppName';
import {useTranslation} from '../../i18n/hooks/useTranslation';
import Logo from '../../logo.svg?react';
import {AppUpdateNotification} from '../../pwa/AppUpdateNotification';
import {PropsWithoutChildren} from '../../react/types';
import {useDeviceState} from '../../responsive/hooks/useDeviceState';
import {UserMenu} from '../../users/UserMenu';
import {LogoWidth} from '../constants';
import {useLayoutOffset} from '../hooks/useLayoutOffset';

export interface PrivateTopBarProps extends PropsWithoutChildren {
  handleNavigationMenuClick: () => void;
}

export const PrivateTopBar = forwardRef<HTMLDivElement, PrivateTopBarProps>(
  ({handleNavigationMenuClick}: PrivateTopBarProps, ref) => {
    const appName = useResponsiveAppName();
    const {isNarrow} = useDeviceState();
    const {horizontalOffset} = useLayoutOffset();
    const {translate} = useTranslation('main');
    return (
      <AppBar
        component="header"
        data-testid="PrivateTopBar"
        position="fixed"
        ref={ref}
        role="banner"
        sx={{zIndex: (theme: Theme) => theme.zIndex.drawer + 1}}
      >
        <Toolbar disableGutters sx={{mr: 2}}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Stack direction="row" sx={{alignItems: 'center'}}>
              {isNarrow && (
                <IconButton
                  aria-label={translate('main:menu')}
                  color="inherit"
                  edge="start"
                  onClick={handleNavigationMenuClick}
                  size="large"
                  sx={{mx: 2, p: 0}}
                >
                  <MenuIcon />
                </IconButton>
              )}
              {!isNarrow && (
                // Dynamically calculate the left margin to center the logo over the side nav:
                <Box sx={{display: 'flex', ml: `calc((${horizontalOffset}px - ${LogoWidth}) / 2)`, mr: '1em'}}>
                  <Logo width={LogoWidth} />
                </Box>
              )}
              <Typography variant="h6">{appName}</Typography>
            </Stack>
            <Stack direction="row" spacing={2} sx={{alignItems: 'center'}}>
              <ComponentBoundary>
                <AppUpdateNotification />
              </ComponentBoundary>
              <ComponentBoundary>
                <UserMenu />
              </ComponentBoundary>
            </Stack>
          </Box>
        </Toolbar>
      </AppBar>
    );
  },
);
PrivateTopBar.displayName = 'TopBar';
